import axios, { AxiosError } from 'axios';
import { toJS } from 'mobx';
import { flow, Instance, types } from 'mobx-state-tree';

import endpoints from '@configs/endpoints';
import {
  AccountsPayableLinkErrorCodes,
  AccountsPayableLinkStatus,
  accountsPayableLinkStep,
  AccountsPayableLinkUpdateStatus,
} from '@constants/accountsPayableLink';
import routes from '@constants/routes';
import Common from '@store/common';
import { formattedAmount } from '@utils/format';

import AccountsPayable, { TxnFirebaseUpdate } from './models/AccountsPayable';
import Errors from './models/Errors';
import Flags from './models/Flags';
import FundInfo from './models/FundInfo';
import AccountsPayableTools from './models/AccountsPayableTools';

interface IErrors extends AxiosError {
  error: boolean;
  msg: string;
  errorCode: string;
}

const AccountsPayableLinkStore = types
  .compose(
    Common,
    types.model({
      accountsPayableLinkId: '',
      businessId: '',
      status: types.optional(
        types.enumeration<AccountsPayableLinkStatus>(Object.values(AccountsPayableLinkStatus)),
        AccountsPayableLinkStatus.UNKNOWN,
      ),
      fundInfo: types.optional(FundInfo, {}),
      accountsPayable: types.optional(AccountsPayable, {}),
      flags: types.optional(Flags, {}),
      errors: types.optional(Errors, {}),
      isSubmitting: false,
      tools: types.optional(AccountsPayableTools, {}),
      partnerName: types.optional(types.string, ''),
    }),
  )
  .views((self) => ({
    get getCurrentStep() {
      return accountsPayableLinkStep[self.status];
    },
    get getAmountCanProcess() {
      const total = self.accountsPayable.canProcess.reduce(
        (total, { amount }) => total + +amount,
        0,
      );
      return `${formattedAmount(total, self.fundInfo.currency)} ${self.fundInfo.currency}`;
    },
    get getAmountCanNotProcess() {
      const total = self.accountsPayable.canNotProcess.reduce(
        (total, { amount }) => total + +amount,
        0,
      );
      return `${formattedAmount(total, self.fundInfo.currency)} ${self.fundInfo.currency}`;
    },
  }))
  .actions((self) => ({
    setCurrentStatus: (status: AccountsPayableLinkStatus) => {
      self.status = status;
    },
    applyFirebaseTxnUpdates: (updates: Record<string, TxnFirebaseUpdate>) => {
      self.accountsPayable.applyFirebaseTxnUpdates(updates);

      // Actualizar el batchStatus en el store si alguno de los deltas lo reporta
      const batchStatuses = Object.values(updates).map((u) => u.batchStatus);
      const lastBatchStatus = batchStatuses[batchStatuses.length - 1] as AccountsPayableLinkStatus;
      if (lastBatchStatus && lastBatchStatus !== self.status) {
        self.status = lastBatchStatus;
      }
    },
    getAccountsPayableLink: flow(function* (accountsPayableLinkId: string, silent = false) {
      if (self.flags.isRequesting) return;

      try {
        self.flags.isRequesting = !silent;
        self.errors.fetchingAccountsPayableLink = false;

        const result = yield axios.get(endpoints.accountsPayableLink(accountsPayableLinkId));
        const { data } = result?.data;
        self.accountsPayableLinkId = data.accountsPayableLinkId;
        self.businessId = data.businessId;
        self.status = data.status;
        self.fundInfo = data.fundInfo;
        self.accountsPayable.updateAccountsPayable(data.accountsPayable, data.status);
        self.tools.updateConfigTools(data?.tools);
        self.partnerName = data.partnerName;
      } catch (error) {
        self.errors.fetchingAccountsPayableLink = true;
        const { response, errorCode, msg } = error as IErrors;
        if (errorCode === AccountsPayableLinkErrorCodes.NOT_FOUND || response?.status === 404) {
          window.location.replace(routes.NOT_FOUND);
        }
        throw new Error(msg || response?.data.msg);
      } finally {
        self.flags.isRequesting = false;
      }
    }),
    updateStatus: flow(function* (status: AccountsPayableLinkUpdateStatus) {
      self.isSubmitting = true;
      const payload = {
        accountsPayableLinkId: self.accountsPayableLinkId,
        status,
      };

      try {
        yield axios.post(endpoints.updateAccountsPayableLinkStatus, payload);
      } catch (error) {
        const { response, msg } = error as IErrors;
        throw new Error(msg || response?.data.msg);
      } finally {
        self.isSubmitting = false;
      }
    }),
    requestFunds: flow(function* (requestEmails: string[]) {
      self.isSubmitting = true;
      const payload = {
        accountsPayableLinkId: self.accountsPayableLinkId,
        requestEmails,
      };

      try {
        yield axios.post(endpoints.requestFunds, payload);
      } catch (error) {
        const { response, msg } = error as IErrors;
        throw new Error(msg || response?.data.msg);
      } finally {
        self.isSubmitting = false;
      }
    }),
  }))
  .actions((self) => ({
    cancelAccountsPayable: flow(function* () {
      self.isSubmitting = true;
      const payload = {
        accountsPayableIds: toJS(self.accountsPayable.getUnselectedIds),
      };

      try {
        const response = yield axios.patch(
          endpoints.cancelAccountsPayable(self.accountsPayableLinkId),
          payload,
        );
      } catch (error) {
        const { response, msg } = error as IErrors;
        throw new Error(msg || response?.data.msg);
      } finally {
        self.isSubmitting = false;
      }
    }),
  }));

export type IAccountsPayableLinkStore = Instance<typeof AccountsPayableLinkStore>;
export default AccountsPayableLinkStore;
